import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import {
  FormInput,
  FormSelect,
  PrimarySecondaryButtonContainer,
  SubmitButton,
} from '@/components/FormComponents';
import { FormErrorAlert } from '@/components/FormErrorAlert';
import { useManageReportingAttribute } from '../hooks/use-manage-reporting-attribute';

const schema = z
  .object({
    advisorId: z
      .string()
      .min(1, 'Advisor ID is required')
      .regex(
        /^[a-zA-Z0-9]{4}-[a-zA-Z0-9]{3}$/,
        'Advisor ID must be in format "xxxx-xxx" (4 alphanumeric characters, hyphen, 3 alphanumeric characters)',
      ),
    attributeLabel: z.string().min(1, 'Attribute label is required'),
    attributeValue: z.string().optional(),
    action: z.enum(['create', 'update', 'delete'], {
      error: 'Please select an action',
    }),
  })
  .refine(
    data => {
      if (data.action === 'create' || data.action === 'update') {
        return !!data.attributeValue;
      }
      return true;
    },
    {
      message: 'Attribute value is required for create and update actions',
      path: ['attributeValue'],
    },
  );

export function ReportingAttributesForm() {
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      action: 'create',
    },
  });

  const { manageAttribute, isPending, isError, errorMessage } = useManageReportingAttribute();

  const selectedAction = watch('action');

  useEffect(() => {
    if (selectedAction === 'delete') {
      setValue('attributeValue', '');
    }
  }, [selectedAction, setValue]);

  const onSubmit = (data: z.infer<typeof schema>) => {
    manageAttribute({
      advisorId: data.advisorId,
      attributeLabel: data.attributeLabel,
      attributeValue: data.attributeValue ?? undefined,
      action: data.action,
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-6 text-gray-700"
    >
      <FormSelect
        label="Action"
        id="action"
        control={control}
        name="action"
        error={errors.action}
        required={true}
        options={[
          { value: 'create', label: 'Create' },
          { value: 'update', label: 'Update' },
          { value: 'delete', label: 'Delete' },
        ]}
        placeholder="Select an action"
      />

      <FormInput
        label="Advisor ID"
        id="advisorId"
        type="text"
        placeholder="Enter advisor ID"
        error={errors.advisorId}
        register={register('advisorId')}
        required={true}
      />

      <FormInput
        label="Attribute Label"
        id="attributeLabel"
        type="text"
        placeholder="Enter attribute label"
        error={errors.attributeLabel}
        register={register('attributeLabel')}
        required={true}
      />

      <FormInput
        label="Attribute Value"
        id="attributeValue"
        type="text"
        placeholder="Enter attribute value"
        error={errors.attributeValue}
        register={register('attributeValue')}
        required={selectedAction === 'create' || selectedAction === 'update'}
        disabled={selectedAction === 'delete'}
      />

      <PrimarySecondaryButtonContainer>
        <SubmitButton
          label={`${selectedAction.charAt(0).toUpperCase() + selectedAction.slice(1)} attribute`}
          className="translate-y-4"
          isLoading={isPending}
          destructive={selectedAction === 'delete'}
        />
      </PrimarySecondaryButtonContainer>

      {isError && (
        <FormErrorAlert
          mainErrorMessage={errorMessage ?? 'An error occurred'}
          className="translate-y-4"
        />
      )}
    </form>
  );
}
